import { SCREEN } from 'router/screens';

import { Config } from 'store/config/reducer.types';
import {
    RBTemplates,
    TemplateviewsById,
} from 'store/railsBuilder/reducer.types';
import { SENTRY_TRANSACTIONS } from 'store/sentry/constants';
import { ParentalControlRating } from 'store/user/reducer.types';

import {
    AssetTypeDetailPageParams,
    DetailsPageParams,
} from 'screens/Detail/types';
import { SearchOptions, SearchType } from 'screens/Search/types';

type GenericRouteParams = {
    name?: keyof TemplateviewsById;
    title?: string;
};

export enum APP_STATES {
    SPLASH = 'SPLASH',
    LANDING = 'LANDING',
    LOGIN = 'LOGIN',
    PERSONALIZING = 'PERSONALIZING',
    READY = 'READY',
}

export type RouteNavigationParams = {
    [SCREEN.SPLASH_SCREEN]: undefined;
    [SCREEN.LOGIN_SCREEN]: undefined;
    [SCREEN.QR_LOGIN_SCREEN]: undefined;
    [SCREEN.FTI_TUTORIAL]: undefined;
    [SCREEN.PERSONALIZE_SCREEN]: undefined;
    [SCREEN.WELCOME_SCREEN]: undefined;
    [SCREEN.LANDING_SCREEN]: undefined;
    [SCREEN.ROOT]: undefined;
    [SCREEN.MODULAR_ROOT]: {
        id: keyof TemplateviewsById;
    };
    [SCREEN.HOME]: {
        id: keyof TemplateviewsById;
    };
    [SCREEN.RECORDINGS]: {
        id: keyof TemplateviewsById;
    };
    [SCREEN.ON_DEMAND]: {
        id: keyof TemplateviewsById;
    };
    [SCREEN.TV_GUIDE]: {
        id: keyof TemplateviewsById;
    };
    [SCREEN.EPG]: {
        id: keyof TemplateviewsById;
    };
    [SCREEN.PAGE]: GenericRouteParams & {
        moduleConfig: Config;
    };
    [SCREEN.COLLECTION]: GenericRouteParams & {
        totalItems?: number;
        templateId?: string;
        railId?: string;
        searchQuery?: string;
        searchType?: SearchType;
        searchOptions?: SearchOptions;
        stripeIndex?: number;
        shouldSetAccessibilityFocus?: boolean;
    };
    [SCREEN.DETAILS]: {
        templateId?: string;
        railId?: string;
    } & DetailsPageParams &
        AssetTypeDetailPageParams;
    [SCREEN.SETTINGS]: { shouldSetAccessibilityFocus?: boolean } | undefined;
    [SCREEN.LANGUAGE]: { shouldSetAccessibilityFocus?: boolean } | undefined;
    [SCREEN.ABOUT]: { shouldSetAccessibilityFocus?: boolean } | undefined;
    [SCREEN.SEARCH]: undefined;
    [SCREEN.PARENTAL_CONTROL_AND_PIN]:
        | { shouldSetAccessibilityFocus?: boolean }
        | undefined;
    [SCREEN.PARENTAL_CONTROL]:
        | { shouldSetAccessibilityFocus?: boolean }
        | undefined;
    [SCREEN.PIN_SETUP]: undefined;
    [SCREEN.PIN_SETUP_INTRODUCTION]: undefined;
    [SCREEN.PIN_SETUP_CREATE]: undefined;
    [SCREEN.PIN_SETUP_SUCCESS]: undefined;
    [SCREEN.PIN_SETUP_UNSUCCESS]: undefined;
    [SCREEN.AUDIO_AND_SUBTITLES]:
        | { shouldSetAccessibilityFocus?: boolean }
        | undefined;
    [SCREEN.SETTINGS_AUDIO]:
        | { shouldSetAccessibilityFocus?: boolean }
        | undefined;
    [SCREEN.SETTINGS_SUBTITLES]:
        | { shouldSetAccessibilityFocus?: boolean }
        | undefined;
    [SCREEN.SETTINGS_SUBTITLES_FONT_SIZE]:
        | { shouldSetAccessibilityFocus?: boolean }
        | undefined;
    [SCREEN.CHANNELS_LINEUP]:
        | { shouldSetAccessibilityFocus?: boolean }
        | undefined;
    [SCREEN.STANDARD_LIST]:
        | { shouldSetAccessibilityFocus?: boolean }
        | undefined;
    [SCREEN.MY_LIST]: { shouldSetAccessibilityFocus?: boolean } | undefined;
    [SCREEN.SWITCH_CHANNEL_LIST]:
        | { shouldSetAccessibilityFocus?: boolean }
        | undefined;
    [SCREEN.EDIT_MY_LIST]:
        | { shouldSetAccessibilityFocus?: boolean }
        | undefined;
    [SCREEN.SETTINGS_RECORDING_QUOTA]: undefined;
    [SCREEN.SETTINGS_INTERNET_CONNECTION]: undefined;
    [SCREEN.SETTINGS_SUBSCRIPTIONS]: undefined;
    [SCREEN.SETTINGS_HELP]: undefined;
    [SCREEN.SETTINGS_LOGOUT]: undefined;
    [SCREEN.SETTINGS_EPG_DISPLAY]: undefined;
    [SCREEN.SETTINGS_EPG_DISPLAY_OPTIONS]: undefined;
    [SCREEN.PROFILES]: undefined;
    [SCREEN.PROFILE_TYPE]: undefined;
    [SCREEN.ADD_PROFILE]: {
        isChildModeEnabled: boolean;
    };
    [SCREEN.PROFILE_AGE_RATING]: {
        isChildModeEnabled: boolean;
        ageRating: {
            rating: ParentalControlRating;
            key: string;
        };
    };
    [SCREEN.PROFILE_AVATARS]: {
        isChildModeEnabled: boolean;
    };
    [SCREEN.MANAGE_PROFILE]: {
        name: string;
    };
    [SCREEN.SETTINGS_BACKEND_INFO]:
        | { shouldSetAccessibilityFocus?: boolean }
        | undefined;
    [SCREEN.SETTINGS_TERMS_CONDITIONS]: undefined;
    [SCREEN.DETAILS_VOD_HIGH_USAGE_RESTRICTION]: undefined;
};

type ModularTemplates =
    | RBTemplates.Home
    | RBTemplates.HomeKids
    | RBTemplates.OnDemand
    | RBTemplates.Recordings;

export type ModularScreens =
    | SCREEN.HOME
    | SCREEN.RECORDINGS
    | SCREEN.ON_DEMAND
    | SCREEN.MODULAR_ROOT;

export const ModularScreensArray: ModularScreens[] = [
    SCREEN.HOME,
    SCREEN.RECORDINGS,
    SCREEN.ON_DEMAND,
    SCREEN.MODULAR_ROOT,
];

export type RootTemplates = ModularTemplates | RBTemplates.TV;

export enum RootPathnames {
    home = 'home',
    tv = 'tv',
    recordings = 'recordings',
    ondemand = 'ondemand',
}

export enum DetailPageMediaType {
    LIVE = 'epg',
    SERIES = 'series',
    VOD = 'vod',
    RECORDING = 'recordings',
}

export const TEMPLATE_TO_ROUTE: Record<
    ModularTemplates | RBTemplates.TV,
    ModularScreens | SCREEN.TV_GUIDE
> = {
    [RBTemplates.Home]: SCREEN.HOME,
    [RBTemplates.HomeKids]: SCREEN.HOME,
    [RBTemplates.TV]: SCREEN.TV_GUIDE,
    [RBTemplates.Recordings]: SCREEN.RECORDINGS,
    [RBTemplates.OnDemand]: SCREEN.ON_DEMAND,
};

export const TEMPLATE_TO_PATHNAME: Record<
    ModularTemplates | RBTemplates.TV,
    RootPathnames
> = {
    [RBTemplates.Home]: RootPathnames.home,
    [RBTemplates.HomeKids]: RootPathnames.home,
    [RBTemplates.TV]: RootPathnames.tv,
    [RBTemplates.Recordings]: RootPathnames.recordings,
    [RBTemplates.OnDemand]: RootPathnames.ondemand,
};

export const TEMPLATE_TO_SENTRY_NAVIGATION: Record<
    ModularTemplates | RBTemplates.TV,
    SENTRY_TRANSACTIONS
> = {
    [RBTemplates.Home]: SENTRY_TRANSACTIONS.PageNavigationHome,
    [RBTemplates.HomeKids]: SENTRY_TRANSACTIONS.PageNavigationHome,
    [RBTemplates.TV]: SENTRY_TRANSACTIONS.PageNavigationEPG,
    [RBTemplates.Recordings]: SENTRY_TRANSACTIONS.PageNavigationRecordings,
    [RBTemplates.OnDemand]: SENTRY_TRANSACTIONS.PageNavigationOnDemand,
};

export const TEMPLATE_TO_SENTRY_RENDERING: Record<
    ModularTemplates | RBTemplates.TV,
    SENTRY_TRANSACTIONS
> = {
    [RBTemplates.Home]: SENTRY_TRANSACTIONS.ModularPageRenderingHome,
    [RBTemplates.HomeKids]: SENTRY_TRANSACTIONS.ModularPageRenderingHome,
    [RBTemplates.TV]: SENTRY_TRANSACTIONS.ShowTVGuide,
    [RBTemplates.Recordings]:
        SENTRY_TRANSACTIONS.ModularPageRenderingRecordings,
    [RBTemplates.OnDemand]: SENTRY_TRANSACTIONS.ModularPageRenderingOnDemand,
};
